import { useMemo } from 'react';
import { useApi } from '../infrastructure/useApi';
import { DEFAULT_API_OPTIONS } from '../infrastructure/defaults';
import { API_METHODS } from '../constants/apiMethods';
import { API_URLS } from '../apiUrls';

/**
 * Hook for accessing Search API methods.
 *
 * Usage:
 * const { search, getSuggestions, cancel } = useSearchApi();
 * await search(params);
 *
 * Cancellation scope:
 * - cancel() applies to requests started by this hook instance.
 * - For independent cancellation scopes, create separate hook instances.
 */
export const useSearchApi = (baseOptions = DEFAULT_API_OPTIONS) => {
	const { call, cancel } = useApi(baseOptions);

	return useMemo(() => {
		const search = ({
			limit,
			page,
			filters,
			searchTerm,
			sort,
			searchType = 'orders',
			Order_By: orderBy,
			...params
		}) =>
			call({
				url: API_URLS.search.search(),
				method: API_METHODS.POST,
				payload: {
					Page_Size: Number.isFinite(limit) ? limit : undefined,
					Page_Number: Number.isFinite(page) ? page + 1 : undefined,
					Search: searchTerm,
					Order_By: searchType === 'orders' ? sort || orderBy : undefined,
					Search_Type: searchType,
					Filters:
						filters?.shipToLocation ||
						filters?.siteLocation ||
						filters?.orderDate ||
						filters?.dueDate
							? {
									Ship_To_Number: filters?.shipToLocation,
									Plant_Code: filters?.siteLocation,
									Order_Date: filters?.orderDate
										? {
												Begin_Date: filters.orderDate.startDate,
												End_Date: filters.orderDate.endDate,
										  }
										: undefined,
									Order_Due_Date: filters?.dueDate
										? {
												Begin_Date: filters.dueDate.startDate,
												End_Date: filters.dueDate.endDate,
										  }
										: undefined,
							  }
							: undefined,
				},
			});

		return {
			search,
			getOrders: (params) => search({ ...params, searchType: 'orders' }),
			getProducts: (params) => search({ ...params, searchType: 'products' }),
			getCounts: (params) => search({ ...params, searchType: 'counts' }),
			getSuggestions: () =>
				call({
					url: API_URLS.search.getSearchSuggestions(),
					method: API_METHODS.GET,
				}),
			cancel,
		};
	}, [call, cancel]);
};
